import { useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { useVerifyEmailChange } from "@orbit/api-client-react";
import { Wordmark } from "../components/Brand";

/**
 * /verify-email?changeRequestId=…&code=…
 *
 * Deep-link landing for the verification email body. We pull the two
 * query params and call /email-change/verify exactly once. On success
 * we surface a friendly confirmation and a back-to-profile link.
 *
 * Idempotency: the server-side handler is idempotent per-link, so a
 * second click after success returns ALREADY_VERIFIED rather than an
 * error — we surface that as the same success message.
 */
export function VerifyEmailPage() {
  const search = useSearch();
  const verifyMut = useVerifyEmailChange();
  const [state, setState] = useState<
    | { kind: "verifying" }
    | { kind: "ok"; newEmail: string }
    | { kind: "fail"; message: string }
  >({ kind: "verifying" });

  useEffect(() => {
    const params = new URLSearchParams(search);
    const id = params.get("changeRequestId");
    const code = params.get("code");
    if (!id || !code) {
      setState({
        kind: "fail",
        message: "This link is missing parts. Open it from the email we sent.",
      });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await verifyMut.mutateAsync({
          changeRequestId: id,
          code,
        });
        if (!cancelled) setState({ kind: "ok", newEmail: r.newEmail });
      } catch (err) {
        if (cancelled) return;
        const e = err as { apiError?: { code?: string; message?: string } };
        if (e.apiError?.code === "ALREADY_VERIFIED") {
          setState({ kind: "ok", newEmail: "your new email" });
        } else {
          setState({
            kind: "fail",
            message:
              e.apiError?.message ??
              "We couldn't verify that link. Try again from the email.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <Wordmark size="md" />
      <div className="mt-8 max-w-sm">
        {state.kind === "verifying" && (
          <p className="text-sm text-ink-dark">Verifying your email…</p>
        )}
        {state.kind === "ok" && (
          <>
            <h1 className="text-2xl font-bold text-navy">Email verified</h1>
            <p className="mt-2 text-sm text-ink-dark">
              {state.newEmail} is now your verified email on Orbit.
            </p>
            <Link
              href="/profile"
              className="mt-6 inline-block btn-primary px-6"
            >
              Back to profile
            </Link>
          </>
        )}
        {state.kind === "fail" && (
          <>
            <h1 className="text-2xl font-bold text-navy">
              Couldn't verify
            </h1>
            <p className="mt-2 text-sm text-red-700">{state.message}</p>
            <Link
              href="/profile"
              className="mt-6 inline-block btn-secondary px-6"
            >
              Back to profile
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
